export const LOCATION_QUERY_PARAM = 'location';

type LocationRouter = {
  isReady?: boolean;
  pathname: string;
  query: unknown;
  replace: (url: unknown, as?: unknown, options?: unknown) => unknown;
};

/** Drop `location` from a Next.js query object. Leaves other params in place. */
export const withoutLocationQuery = (query: Record<string, unknown>): Record<string, unknown> => {
  if (!(LOCATION_QUERY_PARAM in query)) {
    return query;
  }

  const rest = { ...query };
  delete rest[LOCATION_QUERY_PARAM];
  return rest;
};

/**
 * `?location=` is a one-shot handoff (search card → site crumb → search).
 * history.replaceState does not update Next.js router.query, so header/logo
 * `getQuery(query)` would resurrect it. Strip via router.replace instead.
 */
export const stripLocationFromRouter = (router: LocationRouter): void => {
  const query = router.query;
  if (!router.isReady || !query || typeof query !== 'object' || !(LOCATION_QUERY_PARAM in query)) {
    return;
  }

  router.replace(
    { pathname: router.pathname, query: withoutLocationQuery(query as Record<string, unknown>) },
    undefined,
    { shallow: true }
  );
};

/**
 * Carries the geocoded "City, ST" between the search page and a site detail
 * page: search results link to a site with it, and the site breadcrumb links
 * back with it so the search re-runs instead of landing on empty results.
 */
export const withLocationParam = (href: string, locality?: string): string => {
  if (!href || !locality) {
    return href;
  }

  const separator = href.includes('?') ? '&' : '?';
  return `${href}${separator}${LOCATION_QUERY_PARAM}=${encodeURIComponent(locality)}`;
};

/** Location the current page was opened with. Empty on the server and on a cold start. */
export const getLocationParam = (): string => {
  if (typeof window === 'undefined') {
    return '';
  }

  return (new URLSearchParams(window.location.search).get(LOCATION_QUERY_PARAM) ?? '').trim();
};
